import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTransitionHook } from "@uirouter/react";
import debounce from "lodash/debounce";

import { Button, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Box } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks";
import { useTranslation } from "pro-features/components/WithTranslation";

import { trademarksTracking } from "../../../../services/tracking/trademarks";
import { InputError } from "../../../ui/input-error/input-error";
import { InputLabel } from "../../../ui/input-label/input-label";
import { Widget } from "../../../ui/layout/widget";
import { TextArea } from "../../../ui/textarea/textarea";
import { TextField } from "../../../ui/textfield/textfield";
import { Toast } from "../../../ui/toast";
import { useBrandedKeywords } from "../../create-campaign-wizard/hooks/use-branded-keywords";
import { DomainSelect } from "../../domain-input/domain-select";

import { ButtonSkelton, LabelSkelton, TextareaSkelton, TextInputSkelton } from "./form-styled";
import type { ITrademarkConfigsFormProps, ITradeMarkFormData } from "./form-types";
import type { ILeaveCreateProcessConfirmModalProps } from "./leave-create-process-confirm-modal";
import { LeaveCreateProcessConfirmModal } from "./leave-create-process-confirm-modal";
import { validateForm, validateFormField, DATA_FIELDS } from "./validation";

const DOMAIN_ERROR_ID = "myDomain-error";
const WHITELISTED_DOMAINS_ERROR_ID = "whitelistedDomains-error";

export const TrademarkConfigsForm = <T extends ITradeMarkFormData = ITradeMarkFormData>({
    initialData,
    isLoading,
    onSave,
    onCancel,
    footerActions,
    confirmButtonText = "Save changes",
    cancelButtonText = "Cancel",
    isNew,
    readonly,
    confirmButtonTooltipText = "",
}: ITrademarkConfigsFormProps<T>) => {
    const translate = useTranslation();
    const toasts = useToasts();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [formState, setFormState] = useState<T | undefined>(initialData);
    const [formError, setFormError] = useState<{ [K in keyof T]?: string | null }>({});
    const { refetch, isFetching } = useBrandedKeywords(formState?.domain ?? "", [999]);

    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    useTransitionHook("onBefore", {}, (transition) => {
        if (isDirty) {
            transition.abort();
            openModal();
        }
    });

    const debouncedValidateField = useRef(
        debounce((field: keyof T, value: string) => {
            const errorMessage = validateFormField(field as keyof ITradeMarkFormData, value);
            setFormError((prevState) => ({
                ...prevState,
                [field]: errorMessage,
            }));
        }, 300),
    ).current;

    useEffect(() => {
        return () => {
            debouncedValidateField.cancel();
        };
    }, [debouncedValidateField]);

    const onFieldChange = useCallback(
        (field: keyof T) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = event.target;
            setIsDirty(true);
            setFormState((prevState) => ({
                ...(prevState ?? ({} as T)),
                [field]: value,
            }));
            debouncedValidateField(field, value);
        },
        [debouncedValidateField],
    );

    // The picker commits whole domains rather than keystrokes, so debouncing would only delay an
    // error the user has already resolved.
    const handleDomainChange = useCallback((domain: string) => {
        setIsDirty(true);
        setFormState((prevState) => ({
            ...(prevState ?? ({} as T)),
            domain,
        }));
        setFormError((prevState) => ({
            ...prevState,
            domain: validateFormField("domain", domain),
        }));
    }, []);

    const isFormValid = useMemo(() => {
        return validateForm(formState, formError);
    }, [formError, formState]);

    const confirmButtonTooltipTextParsed = useMemo(() => {
        if (!isFormValid) {
            const existingError = Object.values(formError).find((error) => !!error) as string | null;
            if (existingError) {
                return translate(existingError);
            }
            for (const field of DATA_FIELDS) {
                const errorMessage = validateFormField(field as keyof ITradeMarkFormData, formState?.[field] ?? "");
                if (errorMessage) {
                    return translate(errorMessage);
                }
            }
        }
        return confirmButtonTooltipText;
    }, [confirmButtonTooltipText, formError, formState, isFormValid, translate]);

    const handleUpdate = () => {
        setIsDirty(false);
        onSave?.(formState as T);
    };

    const handleCloseModal: ILeaveCreateProcessConfirmModalProps["onClose"] = (
        action: "confirm" | "cancel" | "close",
    ) => {
        if (action === "confirm") {
            setIsDirty(false);
            onCancel?.();
        }

        closeModal();
    };

    const handlePrefillBasedOnDomain = async () => {
        if (!formState?.domain) {
            return;
        }
        trademarksTracking.onPrefillClick();
        try {
            const { data, error } = await refetch();
            if (error) {
                throw error;
            }
            if (data?.length) {
                onFieldChange("variationsAndTypos")({
                    target: { value: [...data].splice(0, 5).join("\n") },
                } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
            } else {
                toasts.showErrorToast(
                    <Toast
                        type="error"
                        text={translate("monitor_and_protect.shared.trademark_config_form.prefill_no_results")}
                    />,
                );
            }
        } catch (error) {
            console.error("error", error);
        }
    };

    return (
        <Widget elevation={null}>
            <Stack
                padding="24px"
                gap="24px"
            >
                <Stack gap="4px">
                    {isLoading ? (
                        <LabelSkelton />
                    ) : (
                        <InputLabel
                            infoTooltip={translate(
                                "monitor_and_protect.shared.trademark_config_form.trademark_name_tooltip",
                            )}
                            htmlFor="trademarkConfigName"
                        >
                            {translate("monitor_and_protect.shared.trademark_config_form.trademark_name_label")}
                        </InputLabel>
                    )}
                    {isLoading ? (
                        <TextInputSkelton />
                    ) : (
                        <TextField
                            value={formState?.name ?? ""}
                            required
                            readOnly={readonly}
                            id="trademarkConfigName"
                            onChange={onFieldChange("name")}
                            placeholder={translate(
                                "monitor_and_protect.shared.trademark_config_form.trademark_name_placeholder",
                            )}
                            hasError={!!formError.name}
                        />
                    )}
                    <InputError hasError={!!formError.name}>
                        {formError.name ? translate(formError.name) : ""}
                    </InputError>
                </Stack>
                <Stack gap="4px">
                    {isLoading ? (
                        <LabelSkelton />
                    ) : (
                        <InputLabel
                            infoTooltip={translate("monitor_and_protect.shared.trademark_config_form.domain_tooltip")}
                        >
                            {translate("monitor_and_protect.shared.trademark_config_form.domain_label")}
                        </InputLabel>
                    )}
                    {isLoading ? (
                        <TextInputSkelton />
                    ) : (
                        <DomainSelect
                            value={formState?.domain ?? ""}
                            onChange={handleDomainChange}
                            isReadOnly={readonly}
                            hasError={!!formError.domain}
                            errorId={DOMAIN_ERROR_ID}
                            ariaLabel={translate("monitor_and_protect.shared.trademark_config_form.domain_label")}
                            placeholder={translate(
                                "monitor_and_protect.shared.trademark_config_form.domain_placeholder",
                            )}
                            dataAutomation="mnp-trademark-config-form-domain"
                        />
                    )}
                    <InputError
                        id={DOMAIN_ERROR_ID}
                        hasError={!!formError.domain}
                    >
                        {formError.domain ? translate(formError.domain) : ""}
                    </InputError>
                </Stack>
                <Stack gap="4px">
                    {isLoading ? (
                        <LabelSkelton />
                    ) : (
                        <InputLabel
                            infoTooltip={
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            "monitor_and_protect.shared.trademark_config_form.whitelisted_domains_tooltip",
                                        ),
                                    }}
                                />
                            }
                            htmlFor="whitelistedDomains"
                        >
                            {translate("monitor_and_protect.shared.trademark_config_form.whitelisted_domains_label")}
                        </InputLabel>
                    )}
                    {isLoading ? (
                        <TextareaSkelton />
                    ) : (
                        <TextArea
                            readOnly={readonly}
                            value={formState?.whiteListedDomains ?? ""}
                            id="whitelistedDomains"
                            onChange={onFieldChange("whiteListedDomains")}
                            placeholder={translate(
                                "monitor_and_protect.shared.trademark_config_form.whitelisted_domains_placeholder",
                            )}
                            rows={4}
                            hasError={!!formError.whiteListedDomains}
                            aria-describedby={formError.whiteListedDomains ? WHITELISTED_DOMAINS_ERROR_ID : undefined}
                        />
                    )}
                    <InputError
                        id={WHITELISTED_DOMAINS_ERROR_ID}
                        hasError={!!formError.whiteListedDomains}
                    >
                        {formError.whiteListedDomains ? translate(formError.whiteListedDomains) : ""}
                    </InputError>
                </Stack>
                <Stack gap="4px">
                    {isLoading ? (
                        <LabelSkelton />
                    ) : (
                        <InputLabel
                            infoTooltip={translate(
                                "monitor_and_protect.shared.trademark_config_form.variations_tooltip",
                            )}
                            htmlFor="variationsAndTypos"
                        >
                            {translate("monitor_and_protect.shared.trademark_config_form.variations_label")}
                        </InputLabel>
                    )}
                    {isLoading ? (
                        <TextareaSkelton />
                    ) : (
                        <TextArea
                            readOnly={readonly}
                            required
                            value={formState?.variationsAndTypos ?? ""}
                            id="variationsAndTypos"
                            onChange={onFieldChange("variationsAndTypos")}
                            placeholder={translate(
                                "monitor_and_protect.shared.trademark_config_form.variations_placeholder",
                            )}
                            rows={10}
                            hasError={!!formError.variationsAndTypos}
                        />
                    )}
                    <InputError hasError={!!formError.variationsAndTypos}>
                        {formError.variationsAndTypos ? translate(formError.variationsAndTypos) : ""}
                    </InputError>
                </Stack>
                {readonly || isLoading ? null : (
                    <Box>
                        <Tooltip
                            content={translate("monitor_and_protect.shared.trademark_config_form.prefill_tooltip")}
                        >
                            <Button
                                type="outlined"
                                onClick={() => void handlePrefillBasedOnDomain()}
                                isDisabled={!formState?.domain || isFetching}
                                isLoading={isFetching}
                            >
                                {translate("monitor_and_protect.shared.trademark_config_form.prefill_button")}
                            </Button>
                        </Tooltip>
                    </Box>
                )}
            </Stack>
            <Widget.Footer
                justifyContent="space-between"
                gap="8px"
                paddingY="8px"
            >
                {isLoading ? (
                    <>
                        <ButtonSkelton />
                        <ButtonSkelton />
                    </>
                ) : (
                    <>
                        <Box>{footerActions ?? null}</Box>
                        <Flex gap="8px">
                            <Button
                                type="flat"
                                onClick={onCancel}
                            >
                                {cancelButtonText}
                            </Button>
                            <Tooltip
                                content={confirmButtonTooltipTextParsed}
                                enabled={!!confirmButtonTooltipTextParsed}
                            >
                                <Button
                                    isLoading={isLoading}
                                    isDisabled={!isFormValid || isLoading || !isDirty || readonly}
                                    hidden={readonly}
                                    onClick={handleUpdate}
                                >
                                    {confirmButtonText}
                                </Button>
                            </Tooltip>
                        </Flex>
                    </>
                )}
            </Widget.Footer>
            <LeaveCreateProcessConfirmModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                trademarkName={formState?.name}
                domain={formState?.domain}
                isNew={isNew}
            />
        </Widget>
    );
};
